/**
 * Camarilla Pivot Points
 *
 * Intraday pivot system computed from the PREVIOUS completed session's
 * high/low/close (Daily / Weekly / Monthly grouping), not the previous bar.
 * Levels stay fixed for the whole session (non-repainting) and are drawn
 * from the current session's first candle to the right edge.
 *
 * Source-session validation: a Weekly/Monthly source is only trusted when the
 * previous session actually spans enough distinct days (>=3 days for Weekly,
 * >=8 for Monthly). Thin/partial slices of history (e.g. loaded data starting
 * mid-week) would otherwise produce a collapsed, unreadable level cluster — in
 * that case the indicator falls back to the previous DAILY session's levels
 * (metadata.sourceUsed reports the fallback).
 *
 * Formulas (standard Camarilla):
 *   PP = (H + L + C) / 3
 *   R1 = C + range*1.1/12   S1 = C - range*1.1/12
 *   R2 = C + range*1.1/6    S2 = C - range*1.1/6
 *   R3 = C + range*1.1/4    S3 = C - range*1.1/4
 *   R4 = C + range*1.1/2    S4 = C - range*1.1/2
 *
 * Strategy signals (closed-bar crossings vs the session's levels):
 *   - Breakout: close crosses above R3/R4 (buy) or below S3/S4 (sell).
 *   - Rejection: first wick through R3/S3 that closes back inside —
 *     the classic Camarilla fade toward the opposite side.
 *
 * Category: Support/Resistance (Strategies tab) | Pane: Overlay
 */
import {
  IndicatorDefinition,
  IndicatorMarker,
  IndicatorParam,
  IndicatorPriceLine,
  PlotData,
} from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { aggregateSessions, sessionKey } from "./cpr/cpr-engine";
import { CprTimeframe, SessionAgg } from "./cpr/types";

export interface CamarillaLevels {
  pp: number;
  r1: number; r2: number; r3: number; r4: number;
  s1: number; s2: number; s3: number; s4: number;
}

export const computeCamarilla = (
  high: number,
  low: number,
  close: number,
): CamarillaLevels => {
  const range = high - low;
  return {
    pp: (high + low + close) / 3,
    r1: close + (range * 1.1) / 12,
    r2: close + (range * 1.1) / 6,
    r3: close + (range * 1.1) / 4,
    r4: close + (range * 1.1) / 2,
    s1: close - (range * 1.1) / 12,
    s2: close - (range * 1.1) / 6,
    s3: close - (range * 1.1) / 4,
    s4: close - (range * 1.1) / 2,
  };
};

const PARAMS: IndicatorParam[] = [
  { key: "source", label: "Levels Source", type: "select", default: "Daily", group: "Levels",
    options: [
      { label: "Daily (Prev Day)", value: "Daily" },
      { label: "Weekly (Prev Week)", value: "Weekly" },
      { label: "Monthly (Prev Month)", value: "Monthly" },
    ] },
  { key: "showPp", label: "Show PP", type: "boolean", default: true, group: "Levels" },
  { key: "showInner", label: "Show R1-R3 / S1-S3", type: "boolean", default: true, group: "Levels" },
  { key: "showOuter", label: "Show R4 / S4 (Extremes)", type: "boolean", default: true, group: "Levels" },
  { key: "showLabels", label: "Show Axis Labels", type: "boolean", default: true, group: "Levels" },
  { key: "lineStyle", label: "Line Style", type: "select", default: "Solid", group: "Levels",
    options: [
      { label: "Solid", value: "Solid" },
      { label: "Dotted", value: "Dotted" },
      { label: "Dashed", value: "Dashed" },
    ] },
  { key: "colorPp", label: "Pivot Color", type: "color", default: "#FF9800", group: "Style" },
  { key: "colorResistance", label: "Resistance Color", type: "color", default: "#EF5350", group: "Style" },
  { key: "colorSupport", label: "Support Color", type: "color", default: "#26A69A", group: "Style" },
  { key: "showBreakoutSignals", label: "Show Breakout Signals", type: "boolean", default: true, group: "Signals" },
  { key: "showRejectionSignals", label: "Show Rejection Signals", type: "boolean", default: false, group: "Signals" },
];

const DEFAULTS: Record<string, any> = Object.fromEntries(
  PARAMS.map(prm => [prm.key, prm.default] as const),
);

const LINE_STYLES: Record<string, number> = { Solid: 0, Dotted: 1, Dashed: 2 };

/** Minimum distinct calendar days a previous session must span to be trusted. */
const MIN_SPAN_DAYS: Record<CprTimeframe, number> = {
  Daily: 1,
  Weekly: 3,
  Monthly: 8,
};

const distinctDayCount = (candles: CandleData[], s: SessionAgg): number => {
  const days = new Set<string>();
  for (let i = s.firstIdx; i <= s.lastIdx; i++) {
    days.add(sessionKey(candles[i].time, "Daily"));
  }
  return days.size;
};

const isAdequate = (
  candles: CandleData[],
  s: SessionAgg | null,
  source: CprTimeframe,
): s is SessionAgg => {
  if (!s) return false;
  if (source === "Daily") return true;
  return distinctDayCount(candles, s) >= MIN_SPAN_DAYS[source];
};

export const camarillaPivotIndicator: IndicatorDefinition = {
  id: "camarilla_pivot",
  name: "Camarilla Pivot Points",
  shortName: "CAM-PVT",
  category: "support_resistance",
  pane: "overlay",
  params: PARAMS,
  plots: [
    { key: "levels", label: "Camarilla Levels", color: "#FF9800", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const p: Record<string, any> = { ...DEFAULTS, ...(params || {}) };
    if (candles.length < 2) return [{ key: "levels", data: [] }];

    const result: PlotData = { key: "levels", data: [] };
    const source: CprTimeframe = p.source;
    const sessions = aggregateSessions(candles, source);
    if (sessions.length === 0) return [result];
    const dailySessions = source === "Daily" ? sessions : aggregateSessions(candles, "Daily");

    const cur = sessions[sessions.length - 1];
    const prevSource = sessions.length >= 2 ? sessions[sessions.length - 2] : null;

    /**
     * Pick the session whose H/L/C feed the levels for a session starting at
     * `boundaryIdx`:
     *   1. the previous source session, when it is adequate (spans enough
     *      distinct days) and has a real range,
     *   2. otherwise the previous completed DAILY session — for the current
     *      session that is the freshest full-range day (daily fallback),
     *      for historical sessions the nearest completed day before it,
     *   3. degenerate data: the previous source session anyway (old behaviour).
     */
    const resolveBase = (
      prevS: SessionAgg | null,
      boundaryIdx: number,
      isCurrent: boolean,
    ): SessionAgg | null => {
      if (prevS && isAdequate(candles, prevS, source) && prevS.high > prevS.low) {
        return prevS;
      }
      if (isCurrent) {
        for (let j = dailySessions.length - 2; j >= 0; j--) {
          const d = dailySessions[j];
          if (d.high > d.low) return d;
        }
        return prevS;
      }
      for (let j = dailySessions.length - 1; j >= 0; j--) {
        const d = dailySessions[j];
        if (d.lastIdx < boundaryIdx && d.high > d.low) return d;
      }
      return prevS;
    };

    const base = resolveBase(prevSource, cur.firstIdx, true);
    if (!base) return [result];
    const levels = computeCamarilla(base.high, base.low, base.close);
    const usedFallback = source !== "Daily" && base !== prevSource;
    const fromTime = base === prevSource
      ? cur.start
      : dailySessions[dailySessions.length - 1].start;
    const lineStyle = LINE_STYLES[p.lineStyle] ?? 0;

    const priceLines: IndicatorPriceLine[] = [];
    const addLine = (
      id: string, price: number, color: string, title: string, width: number,
    ) => {
      priceLines.push({
        id, price, color, title,
        lineStyle,
        lineWidth: width,
        lineVisible: true,
        axisLabelVisible: p.showLabels !== false,
        fromTime,
      });
    };

    if (p.showPp) addLine("pp", levels.pp, p.colorPp, "PP", 2);
    if (p.showInner) {
      addLine("r1", levels.r1, p.colorResistance, "R1", 1);
      addLine("r2", levels.r2, p.colorResistance, "R2", 1);
      addLine("r3", levels.r3, p.colorResistance, "R3", 1);
      addLine("s1", levels.s1, p.colorSupport, "S1", 1);
      addLine("s2", levels.s2, p.colorSupport, "S2", 1);
      addLine("s3", levels.s3, p.colorSupport, "S3", 1);
    }
    if (p.showOuter) {
      addLine("r4", levels.r4, p.colorResistance, "R4", 1);
      addLine("s4", levels.s4, p.colorSupport, "S4", 1);
    }
    result.priceLines = priceLines;

    const markers: IndicatorMarker[] = [];
    const pushUp = (time: number, text: string) => {
      markers.push({ time, position: "belowBar", color: "#00B450", shape: "arrowUp", text });
    };
    const pushDown = (time: number, text: string) => {
      markers.push({ time, position: "aboveBar", color: "#DC3232", shape: "arrowDown", text });
    };

    for (let i = 1; i < sessions.length; i++) {
      const s = sessions[i];
      const lvBase = resolveBase(sessions[i - 1], s.firstIdx, i === sessions.length - 1);
      if (!lvBase) continue;
      const lv = computeCamarilla(lvBase.high, lvBase.low, lvBase.close);
      for (let bi = s.firstIdx; bi <= s.lastIdx; bi++) {
        if (bi === 0) continue;
        const c = candles[bi];
        const pc = candles[bi - 1];

        if (p.showBreakoutSignals) {
          const crossR4 = pc.close <= lv.r4 && c.close > lv.r4;
          const crossR3 = pc.close <= lv.r3 && c.close > lv.r3;
          const crossS4 = pc.close >= lv.s4 && c.close < lv.s4;
          const crossS3 = pc.close >= lv.s3 && c.close < lv.s3;
          if (crossR4) pushUp(c.time, "R4 Break");
          else if (crossR3) pushUp(c.time, "R3 Break");
          if (crossS4) pushDown(c.time, "S4 Break");
          else if (crossS3) pushDown(c.time, "S3 Break");
        }

        if (p.showRejectionSignals) {
          if (pc.high < lv.r3 && c.high >= lv.r3 && c.close < lv.r3) {
            pushDown(c.time, "R3 Rejection");
          }
          if (pc.low > lv.s3 && c.low <= lv.s3 && c.close > lv.s3) {
            pushUp(c.time, "S3 Rejection");
          }
        }
      }
    }
    markers.sort((a, b) => a.time - b.time);
    result.markers = markers;

    const lastClose = candles[candles.length - 1].close;
    result.metadata = {
      levels,
      source: p.source,
      sourceUsed: usedFallback ? `${p.source} → Daily` : p.source,
      bias: lastClose >= levels.pp ? "BULLISH" : "BEARISH",
    };
    return [result];
  },
};
